import { Link } from 'react-router-dom';
import { useLanguage } from './i18n/LanguageContext';

function Career() {
  const { t } = useLanguage();
  const benefits = [
    {
      title: t.career.benefit1Title,
      description: t.career.benefit1Desc,
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
          <path d="M2 17l10 5 10-5M2 12l10 5 10-5"></path>
        </svg>
      )
    },
    {
      title: t.career.benefit2Title,
      description: t.career.benefit2Desc,
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="12" r="10"></circle>
          <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
        </svg>
      )
    },
    {
      title: t.career.benefit3Title,
      description: t.career.benefit3Desc,
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
      )
    }
  ];

  return (
    <div className="page-content">
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
        <img src="/warehouse-team.jpg" alt="Career" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-navy-dark/70 via-navy-primary/60 to-blue-accent/30"></div>

        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-accent/20 rounded-full blur-3xl animate-pulse"></div>

        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-8 animate-fade-in-up">
            <span className="inline-block px-4 py-2 mb-6 text-sm font-medium tracking-widest uppercase bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              {t.career.heroTag}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-4 tracking-tight">
              {t.career.heroTitle}
            </h1>
            <p className="text-xl font-light opacity-90 max-w-2xl mx-auto">
              {t.career.heroSubtitle}
            </p>
          </div>
        </div>
      </section>

      {/* NextLeap Programme Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-8">
          <div className="text-center">
            <h2 className="text-4xl md:text-5xl font-light text-navy-primary mb-6">
              {t.career.programTitle} <span className="font-semibold">{t.career.programTitleBold}</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-accent via-light-blue to-gold-accent mx-auto mb-10 rounded-full"></div>
            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              {t.career.programPara1}
            </p>
            <p className="text-lg leading-relaxed text-gray-600 mb-6">
              {t.career.programPara2}
            </p>
            <p className="text-lg leading-relaxed text-gray-600">
              {t.career.programPara3}
            </p>
          </div>
        </div>
      </section>

      {/* What We Offer Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-accent/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gold-accent/5 rounded-full blur-3xl"></div>

        <div className="max-w-7xl mx-auto px-8 relative">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light text-navy-primary mb-4">{t.career.offerTitle}</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-accent via-light-blue to-gold-accent mx-auto mb-4 rounded-full"></div>
            <p className="text-gray-600 text-lg">{t.career.offerSubtitle}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, idx) => (
              <div
                key={idx}
                className="group relative bg-white p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100 overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-accent to-gold-accent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-blue-accent/5 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                <div className="relative">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-accent/10 to-gold-accent/10 flex items-center justify-center text-blue-accent mb-6 group-hover:scale-110 transition-transform duration-300">
                    {benefit.icon}
                  </div>
                  <h3 className="font-semibold text-xl text-navy-primary mb-4 group-hover:text-blue-accent transition-colors duration-300">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-navy-dark via-navy-primary to-navy-light text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-blue-accent rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-gold-accent rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto px-8 text-center relative">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-4">{t.career.ctaTitle}</h2>
          <p className="text-white/70 text-lg mb-8">
            {t.career.ctaSubtitle}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-accent to-light-blue text-white font-medium hover:shadow-2xl hover:shadow-blue-accent/30 transition-all duration-300 hover:-translate-y-1"
          >
            {t.nav.contactUs}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Career;
